'use client'

import React, { useId, useState, useEffect, useRef } from 'react'
import { CheckIcon, ImagePlusIcon, Loader2 } from 'lucide-react'

import { useCharacterLimit } from '@/hooks/use-character-limit'
import { useFileUpload } from '@/hooks/use-file-upload'
import { useAuthStore } from '@/stores/auth-store'
import { apiClient } from '@/lib/api-client'
import { getAvatarUrl } from '@/lib/avatar-utils'
import { useTheme } from '@/components/theme-provider'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

interface ProfileEditDialogProps {
  children: React.ReactNode
}

export function ProfileEditDialog({ children }: ProfileEditDialogProps) {
  const { user } = useAuthStore()
  const id = useId()
  const [isLoading, setIsLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const maxLength = 180
  const {
    value,
    characterCount,
    handleChange,
    maxLength: limit,
  } = useCharacterLimit({
    maxLength,
    initialValue: user?.bio || '',
  })

  // Check if user is OAuth user (Google auth)
  const isOAuthUser = user?.email && !user?.username?.includes('@')

  // Helper to remove protocol from website URL for display
  const getDisplayWebsite = (website: string | null | undefined) => {
    if (!website) return ''
    return website.replace(/^https?:\/\//, '')
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const formData = new FormData(e.currentTarget)
      const firstName = formData.get('firstName') as string
      const lastName = formData.get('lastName') as string
      const website = formData.get('website') as string

      const updateData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        bio: value.trim(),
        ...(website.trim() && {
          website: website.trim().startsWith('http')
            ? website.trim()
            : `https://${website.trim()}`,
        }),
      }

      const response = await apiClient.updateProfile(updateData)

      // Update the auth store with new user data - merge with existing user data
      const currentUser = useAuthStore.getState().user

      if (currentUser && response) {
        const updatedUser = { ...currentUser, ...response }
        useAuthStore.setState({
          user: updatedUser,
        })
      }

      setOpen(false)
    } catch (error) {
      console.error('Failed to update profile:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="flex flex-col gap-0 overflow-y-visible p-0 sm:max-w-lg [&>button:last-child]:top-3.5">
        <DialogHeader className="contents space-y-0 text-left">
          <DialogTitle className="border-b px-6 py-4 text-base">
            Edit profile
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="sr-only">
          Make changes to your profile here. You can change your photo and set a
          username.
        </DialogDescription>
        <div className="overflow-y-auto">
          <ProfileBg />
          <Avatar />
          <div className="px-6 pt-4 pb-6">
            <form
              id={`profile-form-${id}`}
              className="space-y-4"
              onSubmit={handleSubmit}
            >
              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="flex-1 space-y-2">
                  <Label htmlFor={`${id}-first-name`}>First name</Label>
                  <Input
                    id={`${id}-first-name`}
                    name="firstName"
                    placeholder="First name"
                    defaultValue={user?.firstName || ''}
                    type="text"
                    required
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <Label htmlFor={`${id}-last-name`}>Last name</Label>
                  <Input
                    id={`${id}-last-name`}
                    name="lastName"
                    placeholder="Last name"
                    defaultValue={user?.lastName || ''}
                    type="text"
                    required
                  />
                </div>
              </div>

              {/* Username field - disabled for OAuth users */}
              <div className="*:not-first:mt-2">
                <Label htmlFor={`${id}-username`}>Username</Label>
                <div className="relative">
                  <Input
                    id={`${id}-username`}
                    className="peer pe-9"
                    placeholder="Username"
                    defaultValue={user?.username || ''}
                    type="text"
                    required
                    disabled={isOAuthUser || false}
                  />
                  <div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-3 peer-disabled:opacity-50">
                    <CheckIcon
                      size={16}
                      className="text-emerald-500"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </div>

              {/* Email field - disabled for OAuth users */}
              <div className="*:not-first:mt-2">
                <Label htmlFor={`${id}-email`}>Email</Label>
                <Input
                  id={`${id}-email`}
                  placeholder="Email"
                  defaultValue={user?.email || ''}
                  type="email"
                  required
                  disabled={isOAuthUser || false}
                />
                {isOAuthUser && (
                  <p className="text-muted-foreground text-xs mt-1">
                    Email cannot be changed for Google accounts
                  </p>
                )}
              </div>

              <div className="*:not-first:mt-2">
                <Label htmlFor={`${id}-website`}>Website</Label>
                <div className="flex rounded-md shadow-xs">
                  <span className="border-input bg-background text-muted-foreground -z-10 inline-flex items-center rounded-s-md border px-3 text-sm">
                    https://
                  </span>
                  <Input
                    id={`${id}-website`}
                    name="website"
                    className="-ms-px rounded-s-none shadow-none"
                    placeholder="yourwebsite.com"
                    defaultValue={getDisplayWebsite(user?.website)}
                    type="text"
                  />
                </div>
              </div>
              <div className="*:not-first:mt-2">
                <Label htmlFor={`${id}-bio`}>Biography</Label>
                <Textarea
                  id={`${id}-bio`}
                  placeholder="Write a few sentences about yourself"
                  defaultValue={value}
                  maxLength={maxLength}
                  onChange={handleChange}
                  aria-describedby={`${id}-description`}
                />
                <p
                  id={`${id}-description`}
                  className="text-muted-foreground mt-2 text-right text-xs"
                  role="status"
                  aria-live="polite"
                >
                  <span className="tabular-nums">{limit - characterCount}</span>{' '}
                  characters left
                </p>
              </div>
            </form>
          </div>
        </div>
        <DialogFooter className="border-t px-6 py-4">
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isLoading}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={isLoading}
            onClick={() => {
              const form = document.getElementById(
                `profile-form-${id}`
              ) as HTMLFormElement
              if (form) {
                form.dispatchEvent(
                  new Event('submit', { cancelable: true, bubbles: true })
                )
              }
            }}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ProfileBg() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { theme } = useTheme()

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Configuration similar to Magic UI's Flickering Grid
    const squareSize = 4
    const gridGap = 6
    const flickerChance = 0.0009
    const maxOpacity = 0.3

    // Determine if we're in dark mode
    const isDarkMode = (() => {
      if (theme === 'system') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches
      }
      return theme === 'dark'
    })()

    // Set canvas size to match container
    const updateCanvasSize = () => {
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width
      canvas.height = rect.height
    }

    updateCanvasSize()

    // Calculate grid dimensions
    const cols = Math.floor(canvas.width / (squareSize + gridGap))
    const rows = Math.floor(canvas.height / (squareSize + gridGap))

    // Store square opacities
    const squares = new Float32Array(cols * rows)

    // Initialize squares with random opacity
    for (let i = 0; i < squares.length; i++) {
      squares[i] = Math.random() * maxOpacity
    }

    const drawGrid = () => {
      // Set background and square colors based on theme
      if (isDarkMode) {
        // Dark theme: black background with white squares
        ctx.fillStyle = '#000000'
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        // Draw flickering grid squares
        for (let i = 0; i < cols; i++) {
          for (let j = 0; j < rows; j++) {
            const opacity = squares[i * rows + j] || 0
            if (opacity > 0.02) {
              ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`
              ctx.fillRect(
                i * (squareSize + gridGap),
                j * (squareSize + gridGap),
                squareSize,
                squareSize
              )
            }
          }
        }
      } else {
        // Light theme: light gray background with darker gray squares
        ctx.fillStyle = '#f8f9fa'
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        // Draw flickering grid squares
        for (let i = 0; i < cols; i++) {
          for (let j = 0; j < rows; j++) {
            const opacity = squares[i * rows + j] || 0
            if (opacity > 0.02) {
              ctx.fillStyle = `rgba(96, 165, 250, ${opacity})`
              ctx.fillRect(
                i * (squareSize + gridGap),
                j * (squareSize + gridGap),
                squareSize,
                squareSize
              )
            }
          }
        }
      }
    }

    const updateSquares = () => {
      for (let i = 0; i < squares.length; i++) {
        if (Math.random() < flickerChance) {
          squares[i] = Math.random() * maxOpacity
        }
      }
    }

    let animationFrameId: number

    const animate = () => {
      updateSquares()
      drawGrid()
      animationFrameId = requestAnimationFrame(animate)
    }

    animate()

    // Handle resize
    const handleResize = () => {
      updateCanvasSize()
      // Recalculate grid if needed
      const newCols = Math.floor(canvas.width / (squareSize + gridGap))
      const newRows = Math.floor(canvas.height / (squareSize + gridGap))
      if (newCols !== cols || newRows !== rows) {
        // Re-initialize if grid size changed significantly
        location.reload()
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      cancelAnimationFrame(animationFrameId)
      window.removeEventListener('resize', handleResize)
    }
  }, [theme])

  return (
    <div className="h-32">
      <div className="relative flex size-full items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0 size-full">
          <canvas
            ref={canvasRef}
            className="pointer-events-none size-full"
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      </div>
    </div>
  )
}

function Avatar() {
  const { user } = useAuthStore()
  const [isUploading, setIsUploading] = useState(false)
  const [imageError, setImageError] = useState(false)

  // Get proxied avatar URL if needed
  const proxiedAvatarUrl = getAvatarUrl(user?.avatarUrl)

  // Show avatar if available - handle errors gracefully
  const initialAvatarImage =
    proxiedAvatarUrl && !imageError
      ? [
          {
            name: 'avatar.jpg',
            size: 1528737,
            type: 'image/jpeg',
            url: proxiedAvatarUrl,
            id: 'avatar-123456789',
          },
        ]
      : []

  const [{ files }, { openFileDialog, getInputProps, addFiles }] =
    useFileUpload({
      accept: 'image/*',
      initialFiles: initialAvatarImage,
    })

  const currentImage =
    files[0]?.preview ||
    (proxiedAvatarUrl && !imageError ? proxiedAvatarUrl : null)

  const handleAvatarUpload = async (fileList: FileList) => {
    if (fileList.length === 0) return

    const file = fileList[0]
    if (!file) return

    setIsUploading(true)

    try {
      const response = await apiClient.uploadAvatar(file)

      // Update user in auth store with new avatar URL
      const currentUser = useAuthStore.getState().user
      if (currentUser) {
        useAuthStore.setState({
          user: { ...currentUser, avatarUrl: response.avatarUrl },
        })
      }

      // Update file upload state with new avatar
      if (fileList.length > 0) {
        addFiles(fileList)
      }
    } catch (error) {
      console.error('Failed to upload avatar:', error)
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="-mt-10 px-6">
      <div className="border-background bg-muted relative flex size-20 items-center justify-center overflow-hidden rounded-full border-4 shadow-xs shadow-black/10">
        {currentImage && !imageError && (
          <img
            src={currentImage}
            className="size-full object-cover"
            width={80}
            height={80}
            alt="Profile image"
            referrerPolicy="no-referrer"
            onError={() => {
              setImageError(true)
            }}
            onLoad={() => {
              setImageError(false)
            }}
          />
        )}
        <button
          type="button"
          className="focus-visible:border-ring focus-visible:ring-ring/50 absolute flex size-8 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white transition-[color,box-shadow] outline-none hover:bg-black/80 focus-visible:ring-[3px]"
          onClick={openFileDialog}
          aria-label="Change profile picture"
          disabled={isUploading}
        >
          {isUploading ? (
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          ) : (
            <ImagePlusIcon size={16} aria-hidden="true" />
          )}
        </button>
        <input
          {...getInputProps()}
          className="sr-only"
          aria-label="Upload profile picture"
          onChange={e => {
            if (e.target.files) {
              handleAvatarUpload(e.target.files)
            }
          }}
        />
      </div>
    </div>
  )
}
